import React, { useRef, useState, useCallback } from 'react';
import { uploadFile } from '../../api/client';
import { PreProcessingReport } from './ImportOrdersSection';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface FileUploadAreaProps {
  onProcessingStart: () => void;
  onFileProcessed: (report: PreProcessingReport) => void;
  isProcessing: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  dropZone: {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    backgroundColor: '#fafafa',
  },
  dropZoneActive: {
    border: '2px dashed #1a6b3c',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    backgroundColor: '#f0fdf4',
  },
  dropZoneDisabled: {
    border: '2px dashed #e5e7eb',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center' as const,
    cursor: 'not-allowed',
    backgroundColor: '#f3f4f6',
    opacity: 0.7,
  },
  icon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  },
  mainText: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '0.25rem',
  },
  subText: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  success: {
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '1rem',
  },
  spinner: {
    width: '1rem',
    height: '1rem',
    border: '2px solid #e5e7eb',
    borderTopColor: '#1a6b3c',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  retryButton: {
    marginTop: '0.5rem',
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#1a6b3c',
    backgroundColor: '#fff',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export function FileUploadArea({ onProcessingStart, onFileProcessed, isProcessing }: FileUploadAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      return 'Only .xlsx files are accepted';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File exceeds maximum size of 10 MB';
    }
    return null;
  };

  const handleUpload = useCallback(async (file: File) => {
    setError(null);
    setSuccessMessage(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsUploading(true);
    onProcessingStart();

    try {
      const report = await uploadFile<PreProcessingReport>(
        '/order-import/pre-process',
        file,
        'file',
      );
      setSuccessMessage('File received and processed successfully.');
      onFileProcessed(report);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      const message = apiErr?.message || 'Upload failed. Please try again.';
      setError(message);
    } finally {
      setIsUploading(false);
    }
  }, [onProcessingStart, onFileProcessed]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isProcessing || isUploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleClick = () => {
    if (isProcessing || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRetry = () => {
    setError(null);
    setSuccessMessage(null);
    fileInputRef.current?.click();
  };

  const disabled = isProcessing || isUploading;

  const dropZoneStyle = disabled
    ? styles.dropZoneDisabled
    : isDragOver
      ? styles.dropZoneActive
      : styles.dropZone;

  return (
    <div>
      <div
        style={dropZoneStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Upload XLSX file. Drag and drop or click to browse."
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {isUploading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <span style={{ fontSize: '0.9rem', color: '#374151' }}>Processing file...</span>
          </div>
        ) : (
          <>
            <div style={styles.icon}>📁</div>
            <div style={styles.mainText}>
              Drag & drop an XLSX file here, or click to browse
            </div>
            <div style={styles.subText}>
              Accepts .xlsx files up to 10 MB
            </div>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-hidden="true"
      />

      {error && (
        <div style={styles.error}>
          {error}
          <br />
          <button style={styles.retryButton} onClick={handleRetry}>
            Try Again
          </button>
        </div>
      )}

      {successMessage && (
        <div style={styles.success}>{successMessage}</div>
      )}

      {/* CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
